import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Badge,
  Box,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Add as AddIcon,
  Photo as PhotoIcon,
  CalendarMonth as CalendarIcon,
  Reviews as ReviewsIcon,
  Analytics as AnalyticsIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '@mui/material/styles';
import { useMobileDetect } from '../../hooks/useMobileDetect';

/**
 * Mobile-optimized bottom navigation component
 * Provides easy thumb navigation for mobile users
 */
const MobileBottomNav = ({ notifications = {} }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { shouldUseBottomNavigation, getSafeAreaInsets } = useMobileDetect();

  // Don't render if not appropriate for current device
  if (!shouldUseBottomNavigation()) {
    return null;
  }

  const safeArea = getSafeAreaInsets();

  // Navigation items with badges for notifications
  const navigationItems = [
    {
      label: 'Dashboard',
      icon: DashboardIcon,
      path: '/dashboard',
      badge: notifications.dashboard || 0,
    },
    {
      label: 'Create',
      icon: AddIcon,
      path: '/posts/create',
      badge: 0,
      isAction: true, // Special styling for primary action
    },
    {
      label: 'Photos',
      icon: PhotoIcon,
      path: '/photos',
      badge: notifications.photos || 0,
    },
    {
      label: 'Calendar',
      icon: CalendarIcon,
      path: '/calendar',
      badge: notifications.calendar || 0,
    },
    {
      label: 'Reviews',
      icon: ReviewsIcon,
      path: '/reviews',
      badge: notifications.reviews || 0,
    },
  ];

  // Get current navigation value
  const getCurrentValue = () => {
    const currentItem = navigationItems.find(item => 
      location.pathname.startsWith(item.path)
    );
    return currentItem ? navigationItems.indexOf(currentItem) : 0;
  };

  const handleNavigation = (event, newValue) => {
    if (newValue !== null && newValue < navigationItems.length) {
      navigate(navigationItems[newValue].path);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      >
        <Paper
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: theme.zIndex.appBar,
            background: theme.palette.mode === 'dark' 
              ? 'rgba(15, 23, 42, 0.95)'
              : 'rgba(248, 250, 252, 0.95)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderTop: `1px solid ${theme.palette.divider}`,
            borderRadius: '20px 20px 0 0',
            paddingBottom: safeArea.bottom || '8px',
            boxShadow: theme.palette.mode === 'dark'
              ? '0 -8px 32px 0 rgba(0, 0, 0, 0.3)'
              : '0 -8px 32px 0 rgba(31, 38, 135, 0.15)',
          }}
          elevation={0}
        >
          <BottomNavigation
            value={getCurrentValue()}
            onChange={handleNavigation}
            sx={{
              background: 'transparent',
              '& .MuiBottomNavigationAction-root': {
                minWidth: 'auto',
                padding: '8px 4px 12px',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&.Mui-selected': {
                  '& .MuiBottomNavigationAction-label': {
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                  },
                  '& .MuiSvgIcon-root': {
                    color: theme.palette.primary.main,
                    transform: 'scale(1.1)',
                  },
                },
                '&:not(.Mui-selected)': {
                  '& .MuiBottomNavigationAction-label': {
                    fontSize: '0.6875rem',
                    color: theme.palette.text.secondary,
                  },
                  '& .MuiSvgIcon-root': {
                    color: theme.palette.text.secondary,
                  },
                },
              },
            }}
          >
            {navigationItems.map((item, index) => {
              const IconComponent = item.icon;
              const isSelected = getCurrentValue() === index;
              const isAction = item.isAction;

              return (
                <BottomNavigationAction
                  key={item.path}
                  label={item.label}
                  icon={
                    <motion.div
                      whileTap={{ scale: 0.9 }}
                      whileHover={{ scale: 1.05 }}
                      transition={{ type: 'spring', stiffness: 400 }}
                    >
                      <Box position="relative">
                        {/* Special styling for action button */}
                        {isAction ? (
                          <Box
                            sx={{
                              width: 56,
                              height: 56,
                              borderRadius: '50%',
                              background: theme.palette.primary.main,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginBottom: 1,
                              boxShadow: '0 4px 12px rgba(30, 58, 138, 0.3)',
                              position: 'relative',
                              top: -8,
                              border: `3px solid ${theme.palette.background.paper}`,
                            }}
                          >
                            <IconComponent 
                              sx={{ 
                                color: 'white', 
                                fontSize: '1.75rem' 
                              }} 
                            />
                          </Box>
                        ) : (
                          <>
                            <Badge
                              badgeContent={item.badge}
                              color="error"
                              sx={{
                                '& .MuiBadge-badge': {
                                  fontSize: '0.625rem',
                                  height: '16px',
                                  minWidth: '16px',
                                  right: -6,
                                  top: -2,
                                },
                              }}
                            >
                              <IconComponent 
                                sx={{ 
                                  fontSize: isSelected ? '1.5rem' : '1.25rem',
                                  transition: 'all 0.3s ease',
                                }} 
                              />
                            </Badge>
                            {/* Selection indicator */}
                            {isSelected && (
                              <motion.div
                                layoutId="bottomNavIndicator"
                                style={{
                                  position: 'absolute',
                                  bottom: -4,
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  width: 4,
                                  height: 4,
                                  borderRadius: '50%',
                                  background: theme.palette.primary.main,
                                }}
                                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                              />
                            )}
                          </>
                        )}
                      </Box>
                    </motion.div>
                  }
                  sx={{
                    ...(isAction && {
                      '& .MuiBottomNavigationAction-label': {
                        color: theme.palette.primary.main,
                        fontWeight: 600,
                      },
                    }),
                  }}
                />
              );
            })}
          </BottomNavigation>
        </Paper>
      </motion.div>
    </AnimatePresence>
  );
};

export default MobileBottomNav;